import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import supabase from '../supabaseClient';
import {
  getCachedCommunityAccess,
  persistCommunityContext,
  clearCommunityContext,
} from '../utils/communityAccessCache';
import {
  profileHasPhone,
  stashPhoneReturn,
} from '../utils/signupFunnelRedirect';
import LoaderScreen from './LoaderScreen';
import { recordPlatformAccessEvent } from '../lib/platformAccessEvents';
import { getAdminDemo, endAdminDemo } from '../lib/adminDemoSession';
import { checkSuperAdmin } from '../utils/superAdminUtils';

/**
 * Guards /community/* — session, community manager, community_id, and
 * Users.phone (redirect to /verify-phone when missing).
 */
const CommunityRoute = ({ children }) => {
  const cachedAccess = useMemo(() => getCachedCommunityAccess(), []);
  const [gate, setGate] = useState('checking');
  const [communityName, setCommunityName] = useState(() => cachedAccess?.communityName || null);

  useEffect(() => {
    const checkCommunity = async () => {
      try {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !sessionData?.session?.user) {
          clearCommunityContext();
          setGate('login');
          return;
        }

        const userId = sessionData.session.user.id;

        const liveCache = getCachedCommunityAccess();
        if (liveCache?.userId && liveCache.userId !== userId) {
          clearCommunityContext();
        }

        const demo = getAdminDemo('community', userId);
        if (demo) {
          if (await checkSuperAdmin()) {
            const { data: demoCommunity } = await supabase
              .from('communities')
              .select('name')
              .eq('id', demo.orgId)
              .maybeSingle();
            if (demoCommunity) {
              const demoName = demoCommunity.name || demo.orgName || 'Community';
              persistCommunityContext({ communityName: demoName, managerName: 'Statbox admin', userId });
              setCommunityName(demoName);
              setGate('ok');
              return;
            }
          }
          endAdminDemo();
        }

        const { data: userProfile, error: profileError } = await supabase
          .from('Users')
          .select('community_id, name, email, community_manager, phone')
          .eq('user_id', userId)
          .maybeSingle();

        let resolvedCommunityName = null;
        if (!profileError && userProfile?.community_id) {
          const { data: communityData } = await supabase
            .from('communities')
            .select('name')
            .eq('id', userProfile.community_id)
            .maybeSingle();
          resolvedCommunityName = communityData?.name;
        }

        const managerFlag = userProfile?.community_manager;
        const isManager =
          managerFlag === true ||
          String(managerFlag ?? '').trim().toLowerCase() === 'true';

        if (profileError || !userProfile || !userProfile.community_id || !isManager) {
          clearCommunityContext();
          setGate('login');
          return;
        }

        if (!profileHasPhone(userProfile)) {
          stashPhoneReturn('community');
          setGate('verify-phone');
          return;
        }

        const communityDisplayName = resolvedCommunityName || 'Community';
        persistCommunityContext({
          communityName: communityDisplayName,
          managerName: userProfile.name,
          userId,
        });
        setCommunityName(communityDisplayName);
        setGate('ok');
        recordPlatformAccessEvent({
          surface: 'community',
          routePath: window.location?.pathname || '/community',
          userId,
          waitForSession: false,
          kind: 'visit',
        });
      } catch (error) {
        console.error('Error verifying community access:', error);
        clearCommunityContext();
        setGate('login');
      }
    };

    checkCommunity();
  }, []);

  if (gate === 'checking') {
    return <LoaderScreen subtitle={`Verifying ${communityName || 'community'} access`} />;
  }

  if (gate === 'login') {
    return <Navigate to="/community/login" replace />;
  }

  if (gate === 'verify-phone') {
    return <Navigate to="/verify-phone" replace />;
  }

  return children;
};

export default CommunityRoute;
